<template>
  <div class="Parmsettings-container">  
    <!-- 页面标题 -->
    <div class="page-header">
      <h1><i class="fas fa-sliders-h"></i> 设备参数设置</h1>
      <p>配置设备运行参数，优化设备性能</p>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左侧设备选择面板 -->
      <div class="left-panel">
        <div class="panel-card">
          <div class="panel-header">
            <i class="fas fa-sitemap"></i>
            <span>设备选择</span>
          </div>
          <div class="device-search">
            <el-input
              v-model="deviceSearch"
              placeholder="搜索设备..."
              prefix-icon="Search"
              clearable
            />
          </div>
          <div class="device-tree">
            <el-tree
              ref="deviceTreeRef"
              :data="deviceTreeData"
              :props="treeProps"
              node-key="id"
              :expand-on-click-node="false"
              :highlight-current="true"
              @node-click="handleDeviceSelect"
              :filter-node-method="filterDevice"
            >
              <template #default="{ node, data }">
                <div class="tree-node">
                  <i :class="getDeviceIcon(data.type)"></i>
                  <span class="node-label">{{ node.label }}</span>
                  <span v-if="data.type === 2" class="device-status" :class="getStatusClass(data.status)">
                    {{ getStatusText(data.status) }}
                  </span>
                </div>
              </template>
            </el-tree>
          </div>
        </div>

        <!-- 设备信息卡片 -->
        <div class="panel-card device-info" v-if="selectedDevice">
          <div class="panel-header">
            <i class="fas fa-info-circle"></i>
            <span>设备信息</span>
          </div>
          <div class="info-content">
            <div class="info-item">
              <label>设备名称:</label>
              <span>{{ selectedDevice.name }}</span>
            </div>
            <div class="info-item">
              <label>设备编码:</label>
              <span>{{ selectedDevice.code }}</span>
            </div>
            <div class="info-item">
              <label>设备状态:</label>
              <span :class="getStatusClass(selectedDevice.status)">
                {{ getStatusText(selectedDevice.status) }}
              </span>
            </div>
            <div class="info-item">
              <label>最后更新:</label>
              <span>{{ selectedDevice.lastUpdate }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧参数设置面板 -->
      <div class="right-panel">
        <div class="panel-card">
          <div class="panel-header">
            <i class="fas fa-cogs"></i>
            <span>参数设置</span>
            <div class="header-actions">
              <el-button type="primary" :icon="Refresh" @click="refreshParameters">
                刷新参数
              </el-button>
              <el-button type="success" :icon="Check" @click="saveAllParameters" :loading="saving">
                保存所有参数
              </el-button>
            </div>
          </div>

          <!-- 参数分组标签 -->
          <div class="parameter-tabs">
            <el-tabs v-model="activeTab" type="card">
              <el-tab-pane
                v-for="group in parameterGroups"
                :key="group.id"
                :label="group.name"
                :name="group.id"
              >
                <!-- 参数设置表单 -->
                <div class="parameter-form">
                  <div
                    v-for="param in group.parameters"
                    :key="param.id"
                    class="parameter-item"
                  >
                    <div class="param-header">
                      <span class="param-name">{{ param.name }}</span>
                      <span class="param-id">ID: {{ param.id }}</span>
                    </div>
                    
                    <div class="param-content">
                      <div class="param-controls">
                        <!-- 数值型参数 -->
                        <template v-if="param.type === 'number'">
                          <el-input-number
                            v-model="param.currentValue"
                            :min="param.min"
                            :max="param.max"
                            :step="param.step"
                            :precision="param.precision"
                            controls-position="right"
                            size="large"
                          />
                          <span class="param-unit">{{ param.unit }}</span>
                        </template>

                        <!-- 布尔型参数 -->
                        <template v-else-if="param.type === 'boolean'">
                          <el-switch
                            v-model="param.currentValue"
                            active-text="开启"
                            inactive-text="关闭"
                          />
                        </template>

                        <!-- 枚举型参数 -->
                        <template v-else-if="param.type === 'enum'">
                          <el-select
                            v-model="param.currentValue"
                            placeholder="请选择"
                            size="large"
                          >
                            <el-option
                              v-for="option in param.options"
                              :key="option.value"
                              :label="option.label"
                              :value="option.value"
                            />
                          </el-select>
                        </template>

                        <!-- 字符串型参数 -->
                        <template v-else>
                          <el-input
                            v-model="param.currentValue"
                            :placeholder="`请输入${param.name}`"
                            size="large"
                            show-word-limit
                            :maxlength="param.maxLength"
                          />
                        </template>

                        <el-button
                          type="primary"
                          :icon="Check"
                          @click="saveSingleParameter(param)"
                          :loading="param.saving"
                        >
                          保存
                        </el-button>
                      </div>

                      <!-- 参数信息 -->
                      <div class="param-info">
                        <div class="param-description">
                          {{ param.description }}
                        </div>
                        <div class="param-range" v-if="param.type === 'number'">
                          范围: {{ param.min }} ~ {{ param.max }} {{ param.unit }}
                        </div>
                        <div class="param-default">
                          默认值: {{ param.defaultValue }}{{ param.unit }}
                        </div>
                      </div>
                    </div>

                    <!-- 参数状态指示 -->
                    <div class="param-status">
                      <span class="status-item" :class="{ changed: param.hasChanged }">
                        <i class="fas fa-edit"></i>
                        已修改
                      </span>
                      <span class="status-item" v-if="param.lastModified">
                        <i class="fas fa-clock"></i>
                        最后修改: {{ param.lastModified }}
                      </span>
                    </div>
                  </div>
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>
        </div>

        <!-- 实时监控面板 -->
        <div class="panel-card monitoring-panel">
          <div class="panel-header">
            <i class="fas fa-chart-line"></i>
            <span>$t('message.clientProject.RealTimeMonitor')</span>
          </div>
          <div class="monitoring-content">
            <div class="monitoring-stats">
              <div class="stat-item">
                <div class="stat-value">{{ monitoredParameters.length }}</div>
                <div class="stat-label">监控参数</div>
              </div>
              <div class="stat-item">
                <div class="stat-value" :class="getAlarmClass(alarmCount)">
                  {{ alarmCount }}
                </div>
                <div class="stat-label">报警数量</div>
              </div>
              <div class="stat-item">
                <div class="stat-value">{{ updateTime }}</div>
                <div class="stat-label">最后更新</div>
              </div>
            </div>

            <!-- 监控参数列表 -->
            <div class="monitoring-list">
              <div
                v-for="item in monitoredParameters"
                :key="item.id"
                class="monitoring-item"
                :class="{ alarm: item.isAlarm }"
              >
                <div class="monitor-name">{{ item.name }}</div>
                <div class="monitor-value">{{ item.value }}{{ item.unit }}</div>
                <div class="monitor-trend">
                  <i :class="getTrendIcon(item.trend)"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 操作日志面板 -->
    <div class="log-panel">
      <div class="panel-card">
        <div class="panel-header">
          <i class="fas fa-history"></i>
          <span>操作日志</span>
          <el-button :icon="Download" text @click="exportLogs">导出日志</el-button>
        </div>
        <div class="log-content">
          <el-timeline>
            <el-timeline-item
              v-for="log in operationLogs"
              :key="log.id"
              :timestamp="log.time"
              :type="log.type"
            >
              <div class="log-item">
                <span class="log-action">{{ log.action }}</span>
                <span class="log-operator">{{ log.operator }}</span>
                <span class="log-details">{{ log.details }}</span>
              </div>
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>
    </div>

    <!-- 保存确认对话框 -->
    <el-dialog
      v-model="saveConfirmVisible"
      title="确认保存"
      width="500px"
      :before-close="handleSaveCancel"
    >
      <div class="save-confirm">
        <div class="confirm-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="confirm-content">
          <h3>即将保存参数修改</h3>
          <p>您确定要保存对以下参数的修改吗？</p>
          <div class="changed-params">
            <div
              v-for="param in changedParameters"
              :key="param.id"
              class="changed-item"
            >
              <span class="param-name">{{ param.name }}</span>
              <span class="param-change">
                {{ param.originalValue }} → {{ param.currentValue }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="handleSaveCancel">取消</el-button>
        <el-button type="primary" @click="confirmSave" :loading="saving">
          确认保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref,  computed, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {  Refresh, Check, Download } from '@element-plus/icons-vue'

// 设备树数据
const deviceTreeData = ref([
  {
    id: 1,
    label: '河北磁台科技有限公司',
    type: 1,
    children: [
      {
        id: 2,
        label: '生产一部',
        type: 0,
        children: [
          {
            id: 3,
            label: '生产线A',
            type: 2,
            code: 'SC001',
            status: 1,
            lastUpdate: '2024-01-20 10:30:25'
          },
          {
            id: 4,
            label: '生产线B',
            type: 2,
            code: 'SC002',
            status: 2,
            lastUpdate: '2024-01-20 09:15:40'
          }
        ]
      },
      {
        id: 5,
        label: '生产二部',
        type: 0,
        children: [
          {
            id: 6,
            label: '检测设备',
            type: 2,
            code: 'JC001',
            status: 1,
            lastUpdate: '2024-01-20 11:20:15'
          }
        ]
      }
    ]
  }
])

// 设备搜索
const deviceSearch = ref('')
const deviceTreeRef = ref()

// 树配置
const treeProps = {
  children: 'children',
  label: 'label'
}

// 选中的设备
const selectedDevice = ref(null)

// 参数分组数据
const parameterGroups = ref([
  {
    id: 'basic',
    name: '基本参数',
    parameters: [
      {
        id: 'speed',
        name: '运行速度',
        type: 'number',
        currentValue: 1200,
        defaultValue: 1000,
        min: 500,
        max: 2000,
        step: 50,
        precision: 0,
        unit: 'rpm',
        description: '设备运行速度设置，影响生产效率和设备寿命',
        lastModified: '2024-01-20 09:30:15',
        saving: false
      },
      {
        id: 'temperature',
        name: '工作温度',
        type: 'number',
        currentValue: 75.5,
        defaultValue: 80.0,
        min: 50,
        max: 120,
        step: 0.5,
        precision: 1,
        unit: '°C',
        description: '设备工作温度设置，超出范围可能影响产品质量',
        lastModified: '2024-01-20 10:15:30',
        saving: false
      }
    ]
  },
  {
    id: 'advanced',
    name: '高级参数',
    parameters: [
      {
        id: 'auto_mode',
        name: '自动模式',
        type: 'boolean',
        currentValue: true,
        defaultValue: true,
        description: '启用自动运行模式，设备根据预设参数自动调整',
        lastModified: '2024-01-20 08:45:20',
        saving: false
      },
      {
        id: 'operation_mode',
        name: '运行模式',
        type: 'enum',
        currentValue: 'mode2',
        defaultValue: 'mode1',
        options: [
          { label: '模式一：标准生产', value: 'mode1' },
          { label: '模式二：高效生产', value: 'mode2' },
          { label: '模式三：节能运行', value: 'mode3' }
        ],
        description: '选择设备运行模式，不同模式对应不同的性能参数',
        lastModified: '2024-01-20 11:05:45',
        saving: false
      }
    ]
  },
  {
    id: 'safety',
    name: '安全参数',
    parameters: [
      {
        id: 'max_pressure',
        name: '最大压力',
        type: 'number',
        currentValue: 25.0,
        defaultValue: 20.0,
        min: 10,
        max: 50,
        step: 0.5,
        precision: 1,
        unit: 'MPa',
        description: '设备最大工作压力限制，安全保护参数',
        lastModified: '2024-01-20 07:30:10',
        saving: false
      },
      {
        id: 'emergency_stop',
        name: '急停响应',
        type: 'boolean',
        currentValue: false,
        defaultValue: false,
        description: '急停按钮响应设置，启用后设备立即停止运行',
        lastModified: '2024-01-20 12:20:35',
        saving: false
      }
    ]
  }
])

// 活动标签页
const activeTab = ref('basic')

// 保存状态
const saving = ref(false)
const saveConfirmVisible = ref(false)

// 监控参数
const monitoredParameters = ref([
  { id: 1, name: '实时速度', value: 1180, unit: 'rpm', trend: 'up', isAlarm: false },
  { id: 2, name: '当前温度', value: 76.2, unit: '°C', trend: 'down', isAlarm: false },
  { id: 3, name: '系统压力', value: 24.5, unit: 'MPa', trend: 'stable', isAlarm: true },
  { id: 4, name: '运行功率', value: 45.8, unit: 'kW', trend: 'up', isAlarm: false }
])

// 操作日志
const operationLogs = ref([
  {
    id: 1,
    time: '2024-01-20 14:30:25',
    type: 'success',
    action: '参数修改',
    operator: '张三',
    details: '修改运行速度: 1000 → 1200 rpm'
  },
  {
    id: 2,
    time: '2024-01-20 14:25:10',
    type: 'primary',
    action: '参数读取',
    operator: '系统',
    details: '从设备读取最新参数值'
  },
  {
    id: 3,
    time: '2024-01-20 14:20:35',
    type: 'warning',
    action: '报警清除',
    operator: '李四',
    details: '清除温度过高报警'
  }
])

// 计算属性
const changedParameters = computed(() => {
  const changed = []
  parameterGroups.value.forEach(group => {
    group.parameters.forEach(param => {
      if (param.hasChanged) {
        changed.push({
          id: param.id,
          name: param.name,
          originalValue: param.defaultValue,
          currentValue: param.currentValue,
          unit: param.unit || ''
        })
      }
    })
  })
  return changed
})

const alarmCount = computed(() => {
  return monitoredParameters.value.filter(item => item.isAlarm).length
})

const updateTime = computed(() => {
  return new Date().toLocaleTimeString()
})

// 方法
const getDeviceIcon = (type) => {
  const icons = {
    1: 'fas fa-building',
    2: 'fas fa-industry',
    0: 'fas fa-folder'
  }
  return icons[type] || 'fas fa-cube'
}

const getStatusClass = (status) => {
  const classes = {
    1: 'status-online',
    2: 'status-offline',
    3: 'status-alarm'
  }
  return classes[status] || 'status-unknown'
}

const getStatusText = (status) => {
  const texts = {
    1: '在线',
    2: '离线',
    3: '报警'
  }
  return texts[status] || '未知'
}

const getTrendIcon = (trend) => {
  const icons = {
    up: 'fas fa-arrow-up trend-up',
    down: 'fas fa-arrow-down trend-down',
    stable: 'fas fa-minus trend-stable'
  }
  return icons[trend] || 'fas fa-minus'
}

const getAlarmClass = (count) => {
  return count > 0 ? 'alarm-count' : 'normal-count'
}

// 设备选择处理
const handleDeviceSelect = (data) => {
  if (data.type === 2) {
    selectedDevice.value = data
    ElMessage.success(`已选择设备: ${data.label}`)
    // 这里可以加载设备的参数数据
    loadDeviceParameters(data.id)
  }
}

// 设备过滤
const filterDevice = (value, data) => {
  if (!value) return true
  return data.label.toLowerCase().includes(value.toLowerCase())
}

// 加载设备参数
const loadDeviceParameters = (deviceId) => {
  // 模拟加载设备参数
  console.log(`加载设备 ${deviceId} 的参数`)
  // 这里可以调用API获取设备的具体参数
}

// 刷新参数
const refreshParameters = () => {
  ElMessage.info('正在刷新参数...')
  // 模拟刷新操作
  setTimeout(() => {
    ElMessage.success('参数刷新完成')
  }, 1000)
}

// 保存单个参数
const saveSingleParameter = async (param) => {
  param.saving = true
  try {
    // 模拟保存操作
    await new Promise(resolve => setTimeout(resolve, 800))
    param.lastModified = new Date().toLocaleString()
    param.hasChanged = false
    ElMessage.success(`参数 ${param.name} 保存成功`)
    
    // 记录操作日志
    operationLogs.value.unshift({
      id: Date.now(),
      time: new Date().toLocaleString(),
      type: 'success',
      action: '参数修改',
      operator: '当前用户',
      details: `修改 ${param.name}: ${param.defaultValue} → ${param.currentValue}${param.unit || ''}`
    })
  } catch (error) {
    ElMessage.error(`参数 ${param.name} 保存失败`)
  } finally {
    param.saving = false
  }
}

// 保存所有参数
const saveAllParameters = () => {
  if (changedParameters.value.length === 0) {
    ElMessage.warning('没有需要保存的参数修改')
    return
  }
  saveConfirmVisible.value = true
}

// 确认保存
const confirmSave = async () => {
  saving.value = true
  try {
    // 模拟批量保存操作
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    parameterGroups.value.forEach(group => {
      group.parameters.forEach(param => {
        if (param.hasChanged) {
          param.lastModified = new Date().toLocaleString()
          param.hasChanged = false
        }
      })
    })
    
    ElMessage.success('所有参数保存成功')
    saveConfirmVisible.value = false
    
    // 记录操作日志
    operationLogs.value.unshift({
      id: Date.now(),
      time: new Date().toLocaleString(),
      type: 'success',
      action: '批量保存',
      operator: '当前用户',
      details: `批量保存 ${changedParameters.value.length} 个参数`
    })
  } catch (error) {
    ElMessage.error('参数保存失败')
  } finally {
    saving.value = false
  }
}

// 取消保存
const handleSaveCancel = () => {
  saveConfirmVisible.value = false
}

// 导出日志
const exportLogs = () => {
  ElMessage.info('开始导出操作日志...')
  // 这里可以实现日志导出功能
}

// 监听参数变化
watch(parameterGroups, (newVal) => {
  newVal.forEach(group => {
    group.parameters.forEach(param => {
      param.hasChanged = param.currentValue !== param.defaultValue
    })
  })
}, { deep: true })

// 监听设备搜索
watch(deviceSearch, (val) => {
  deviceTreeRef.value?.filter(val)
})

onMounted(() => {
  // 初始化时可以选择第一个设备
  if (deviceTreeData.value.length > 0) {
    const firstDevice = findFirstDevice(deviceTreeData.value)
    if (firstDevice) {
      selectedDevice.value = firstDevice
      loadDeviceParameters(firstDevice.id)
    }
  }
})

// 查找第一个设备节点
const findFirstDevice = (nodes) => {
  for (const node of nodes) {
    if (node.type === 2) {
      return node
    }
    if (node.children) {
      const found = findFirstDevice(node.children)
      if (found) return found
    }
  }
  return null
}
</script>

<style scoped>
.settings-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  background: white;
  padding: 30px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.page-header h1 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 2.2em;
}

.page-header p {
  color: #7f8c8d;
  font-size: 1.1em;
}

.main-content {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.panel-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.panel-header {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ebeef5;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
}

.panel-header i {
  margin-right: 10px;
  color: #409EFF;
  font-size: 1.2em;
}

.panel-header span {
  font-weight: 600;
  color: #2c3e50;
  flex: 1;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 左侧面板样式 */
.device-search {
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
}

.device-tree {
  padding: 10px;
  min-height: 300px;
  max-height: 400px;
  overflow-y: auto;
}

.tree-node {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.node-label {
  flex: 1;
}

.device-status {
  font-size: 0.8em;
  padding: 2px 8px;
  border-radius: 10px;
  color: white;
}

.status-online { background: #67C23A; }
.status-offline { background: #909399; }
.status-alarm { background: #F56C6C; }

.device-info {
  margin-top: 20px;
}

.info-content {
  padding: 20px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item label {
  font-weight: 500;
  color: #5d6d7e;
}

/* 右侧面板样式 */
.parameter-tabs {
  padding: 20px;
}

.parameter-item {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.parameter-item:hover {
  border-color: #409EFF;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.param-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.param-name {
  font-size: 1.2em;
  font-weight: 600;
  color: #2c3e50;
}

.param-id {
  font-size: 0.9em;
  color: #909399;
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 4px;
}

.param-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.param-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.param-unit {
  color: #909399;
  font-weight: 500;
}

.param-info {
  color: #5d6d7e;
}

.param-description {
  margin-bottom: 8px;
  line-height: 1.5;
}

.param-range, .param-default {
  font-size: 0.9em;
  color: #909399;
}

.param-status {
  display: flex;
  gap: 20px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.status-item {
  font-size: 0.9em;
  color: #909399;
}

.status-item.changed {
  color: #E6A23C;
  font-weight: 500;
}

/* 监控面板样式 */
.monitoring-content {
  padding: 20px;
}

.monitoring-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-value {
  font-size: 2em;
  font-weight: bold;
  margin-bottom: 8px;
}

.normal-count { color: #67C23A; }
.alarm-count { color: #F56C6C; }

.stat-label {
  color: #909399;
  font-size: 0.9em;
}

.monitoring-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.monitoring-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #67C23A;
}

.monitoring-item.alarm {
  border-left-color: #F56C6C;
  background: #fef0f0;
}

.monitor-name {
  font-weight: 500;
  color: #2c3e50;
}

.monitor-value {
  font-weight: bold;
  color: #409EFF;
}

.monitor-trend .trend-up { color: #F56C6C; }
.monitor-trend .trend-down { color: #67C23A; }
.monitor-trend .trend-stable { color: #909399; }

/* 日志面板样式 */
.log-panel {
  margin-top: 20px;
}

.log-content {
  padding: 20px;
  max-height: 300px;
  overflow-y: auto;
}

.log-item {
  display: flex;
  gap: 15px;
  align-items: center;
}

.log-action {
  font-weight: 500;
  color: #2c3e50;
}

.log-operator {
  color: #409EFF;
  background: #ecf5ff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.9em;
}

.log-details {
  color: #5d6d7e;
  flex: 1;
}

/* 保存确认对话框样式 */
.save-confirm {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.confirm-icon {
  font-size: 3em;
  color: #E6A23C;
}

.confirm-content h3 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.confirm-content p {
  color: #5d6d7e;
  margin-bottom: 15px;
}

.changed-params {
  max-height: 200px;
  overflow-y: auto;
}

.changed-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.param-change {
  color: #E6A23C;
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr;
  }
  
  .param-content {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .monitoring-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .param-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .monitoring-stats {
    grid-template-columns: 1fr;
  }
  
  .header-actions {
    flex-direction: column;
  }
}
</style>